import React, { useState } from 'react';
import classesGlobal from '../../../assets/Global.module.css';
import classes from './Details.module.css';
import Input from '../../../assets/essentials/Input';
import PutForm from '../../../api/internal/PutForm';
import Button from '../../../assets/essentials/Button';
import Form from '../../../assets/essentials/Form';
import PostBody from '../../../api/internal/PostBody';
import EditPayableLineItems from './EditPayableLineItems';
import { useRoleAuthorization } from '../../../hooks/useRoleAuthorization';
import { ADMIN_AUTHORIZED_ROLES } from '../../../auth/AuthRoles';

type PayableDetailsProps = {
  data: PayableDetails;
  vendorAssignmentId: number;
  RefreshPage: Function;
  isLoadlessBill: boolean; //loadless bills are handled differently than bills attached to loads, only allow editing loadless bills
};

function Details({
  data,
  vendorAssignmentId,
  RefreshPage,
  isLoadlessBill = false,
}: PayableDetailsProps) {
  const [isQuickBooksPostPending, setIsQuickBooksPostPending] =
    useState<boolean>(false);
  const [editLineItemsIsOpen, setEditLineItemsIsOpen] =
    useState<boolean>(false);
  const isUserAdmin = useRoleAuthorization(ADMIN_AUTHORIZED_ROLES);

  function UpdatePayableDetails(e) {
    e.preventDefault();
    const formData = new FormData(e.target);
    const appendix = {
      vendorAssignmentId: vendorAssignmentId,
    };
    PutForm(`/Accounting/UpdatePayableDetails`, formData, appendix).then(() => {
      RefreshPage('Details', ['Summary', 'Audit']);
    });
  }

  const hasBeenPostedToQuickbooks = data?.quickBooksBillId != null;

  const CreateQuickbooksBillIdButton = () => {
    function PostBillToQuickbooks() {
      setIsQuickBooksPostPending(true);
      PostBody('Accounting/PostBillToQuickBooks', {
        vendorAssignmentId: vendorAssignmentId,
      })
        .then(response => {
          if (response) {
            RefreshPage('Details', ['Audit']);
          }
        })
        .finally(() => {
          setIsQuickBooksPostPending(false);
        });
    }
    const billHasBeenSentToQuickbooks = data?.quickBooksBillId != null;
    const isDisabled = isQuickBooksPostPending || billHasBeenSentToQuickbooks;

    return (
      <div className={`${classesGlobal.marginTop05Rem}`}>
        {isQuickBooksPostPending ? (
          <Button disabled>Pending...</Button>
        ) : (
          <Button
            variant="good"
            disabled={isDisabled}
            onClick={() => PostBillToQuickbooks()}
            type="button"
          >
            {billHasBeenSentToQuickbooks
              ? `QuickBooks Bill ID: ${data?.quickBooksBillId}`
              : 'Post Bill To QuickBooks'}
          </Button>
        )}
      </div>
    );
  };

  return (
    <>
      <Form onSubmit={e => UpdatePayableDetails(e)}>
        <div className={classesGlobal.attribute}>
          <label>Vendor</label>
          <Input disabled type="text" value={data?.vendorName} />
        </div>
        <div className={`${classesGlobal.attribute} ${classesGlobal.span2}`}>
          <label>Payment Terms</label>
          <Input disabled type="text" value={data?.paymentTerm} />
        </div>
        <div className={`${classesGlobal.attribute} ${classesGlobal.span3}`}>
          <label htmlFor="billName">Bill Name</label>
          <Input
            type="text"
            defaultValue={data?.billName}
            name="billName"
            id="billName"
          />
        </div>
        <div className={`${classesGlobal.attribute}`}>
          <label htmlFor="billDate">Bill Date</label>
          <Input
            type="date"
            defaultValue={data?.billDate}
            name="billDate"
            id="billDate"
          />
        </div>
        <div className={`${classesGlobal.attribute} ${classesGlobal.span2}`}>
          <label htmlFor="dueDate">Due Date</label>
          <Input
            type="date"
            defaultValue={data?.dueDate}
            name="dueDate"
            id="dueDate"
          />
        </div>
        <div className={`${classesGlobal.attribute}`}>
          <label htmlFor="quickBooksBillId">QuickBooks Bill ID</label>
          <Input
            type="number"
            defaultValue={data?.quickBooksBillId}
            name="quickBooksBillId"
            id="quickBooksBillId"
          />
        </div>
        <div className={`${classesGlobal.attribute} ${classesGlobal.span2}`}>
          <label className={classesGlobal.marginTop05Rem} />
          <CreateQuickbooksBillIdButton />
        </div>
        <div className={classes.buttonWithMessage}>
          <Button type="submit" variant="good" disabled={true}>
            Save Changes
          </Button>
        </div>
      </Form>
      {isLoadlessBill && (
        <div className={classes.buttonContainer}>
          <div className={classes.buttonWithMessage}>
            <Button
              onClick={() => setEditLineItemsIsOpen(!editLineItemsIsOpen)}
              disabled={!isUserAdmin || hasBeenPostedToQuickbooks}
            >
              Edit Line Items
            </Button>
            {!isUserAdmin && (
              <div
                className={`${classes.disabledMessage} ${classes.disabledMessageWarning}`}
              >
                Admin access required to edit bill line items
              </div>
            )}
            {hasBeenPostedToQuickbooks && isUserAdmin && (
              <div
                className={`${classes.disabledMessage} ${classes.disabledMessageError}`}
              >
                Cannot edit line items after posting to QuickBooks.
              </div>
            )}
          </div>
        </div>
      )}
      {editLineItemsIsOpen && (
        <EditPayableLineItems
          lineItems={data?.lineItems}
          vendorAssignmentId={vendorAssignmentId}
          onUpdate={() => {
            RefreshPage('Details', ['Summary', 'Audit']);
          }}
        />
      )}
    </>
  );
}
export default Details;
